import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { CheckoutEventsData } from '@paddle/paddle-js';
import * as Sentry from '@sentry/angular-ivy';
import { BehaviorSubject, combineLatest, lastValueFrom, Subject, take, timer } from 'rxjs';
import { CheckoutV2Component } from 'src/app/components/new-billing/checkout-v2/checkout-v2.component';
import {
  CheckoutComponent,
  CheckoutDialogClose,
  CheckoutDialogData
} from 'src/app/components/new-billing/checkout/checkout.component';
import { AddCreditCardStep } from 'src/app/components/starter-sidebar/items/add-credit-card';
import { CLOSE_DIALOG_DELAY } from 'src/app/constants/billing.constants';
import { UpdateBillingDetails } from 'src/app/pages/new-billing/store/actions';
import { CHOOSE_PLAN_INTENT_QUERY_PARAM } from 'src/app/pages/new-billing/utils/explicit-plan-selection.utils';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingApiData, BillingDetailsApiData, CancellationRequest } from 'src/app/pages/new-billing/types/api.types';
import { BillingService } from 'src/app/services/billing/billing.service';
import { CompanySettingsChanged } from 'src/app/store/auth/auth.actions';
import { CompleteWelcomeStep } from 'src/app/store/onboarding/onboarding.actions';

type OpenCheckoutDialogOptions = {
  checkoutSuccessCallback?: CheckoutDialogData['onCheckoutSuccess'];
  socialProof?: boolean;
}
  & Pick<
    CheckoutDialogData,
    'currentPlan'
    | 'isUpdatingCard'
    | 'planId'
    | 'paymentFailureSummaryData'
    | 'withOverlayLoader'
    | 'trackingCtx'
    | 'showTrialHeadsUpBanner'
  >;

export type OpenSnackbarOptions = {
  action?: string;
  duration?: number;
  message: string;
  onActionCallback?: () => void;
};

export const USERS_SNACKBAR_DURATION = 5000;

@Injectable({
  providedIn: 'root',
})
export class BillingActionsService implements OnDestroy {
  private _isSubmitting$ = new BehaviorSubject(false);
  private readonly destroy$ = new Subject<void>();

  get isSubmitting$() {
    return this._isSubmitting$.asObservable();
  }

  constructor(
    private billingService: BillingService,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private router: Router,
    private store: Store,
    private translateService: TranslateService,
  ) { }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this._isSubmitting$.complete();
  }

  public setIsSubmitting(isSubmitting: boolean) {
    this._isSubmitting$.next(isSubmitting);
  }

  public async onCardUpdatedCompleted(
    { payment }: CheckoutEventsData,
    dialogRef: MatDialogRef<any, any>,
  ) {
    const pollCondition = (billingDetails: BillingDetailsApiData) =>
      billingDetails.cardLastFourDigits === payment.method_details.card.last4;

    const timer$ = timer(CLOSE_DIALOG_DELAY);
    const pollBillingDetails$ = this.billingService.pollBillingDetailsUntilConditionMet(pollCondition);

    combineLatest([timer$, pollBillingDetails$]).pipe(
      take(1),
    ).subscribe({
      next: ([, billingDetails]) => {
          this.store.dispatch(new CompleteWelcomeStep(AddCreditCardStep.stepId));
          this.store.dispatch(new UpdateBillingDetails(billingDetails));
          dialogRef.close({ billingDetails });

          const currentUrl = this.router.url;
          const urlTree = this.router.parseUrl(currentUrl);
          const intentKey = CHOOSE_PLAN_INTENT_QUERY_PARAM as string;
          let updated = false;
          if (urlTree.queryParams['choosePlan']) {
            delete urlTree.queryParams['choosePlan'];
            updated = true;
          }
          if (urlTree.queryParams[intentKey]) {
            delete urlTree.queryParams[intentKey];
            updated = true;
          }
          if (updated) {
            this.router.navigateByUrl(urlTree);
          }
      },
      error: (error) => {
        this.handleCardUpdateError(payment.method_details.card.last4, dialogRef);
      },
    });
  }

  private handleCardUpdateError(cardLast4: string, dialogRef: MatDialogRef<CheckoutComponent, Pick<BillingApiData, 'billingDetails'>>) {
    try {
      const rawBillingDetails = this.store.selectSnapshot(NewBillingState.updatedBillingDetails);
      const billingDetails = { ...rawBillingDetails, cardLastFourDigits: cardLast4 };
      this.store.dispatch(new CompleteWelcomeStep(AddCreditCardStep.stepId));
      this.store.dispatch(new UpdateBillingDetails(billingDetails));
      dialogRef.close({ billingDetails });
    } catch (error) {
      Sentry.captureMessage('Error handling card update error', {
        level: 'error',
        extra: { error },
      });
      dialogRef.close();
    }
  }

  public async onUsersAdded(prevUserCount: number, invitedEmails: string[]) {
    const pollCondition = ({ quantity }: BillingDetailsApiData) => quantity > prevUserCount;

    try {
      const billingDetails = await lastValueFrom(this.billingService.pollBillingDetailsUntilConditionMet(pollCondition));
      this.store.dispatch(new UpdateBillingDetails(billingDetails));

      // Optimistic update: Update userCount immediately for UI feedback
      // Socket handler will correct this if websocket update arrives later
      // Use prevUserCount to avoid race condition with websocket updates
      setTimeout(() => {
        const userCount = prevUserCount + invitedEmails.length;
        this.store.dispatch(new CompanySettingsChanged({ userCount }, null, null));
      }, 0);

      this.openSnackbar({
        message: this.translateService.instant('billing.inviteSuccessMsg', { invitedCount: invitedEmails.length }),
        action: this.translateService.instant('billing.goToUsers'),
        onActionCallback: () => this.router.navigate(['/manage-users']),
        duration: USERS_SNACKBAR_DURATION,
      });
    } catch (e) {
      Sentry.captureMessage('Polling for billing details timed out after adding users', {
        level: 'error',
        extra: { error: e },
      });

      // Fallback: Update userCount manually if polling fails (websocket might also be delayed/failed)
      // Use prevUserCount to avoid race condition with websocket updates
      setTimeout(() => {
        const userCount = prevUserCount + invitedEmails.length;
        this.store.dispatch(new CompanySettingsChanged({ userCount }, null, null));
      }, 0);

      // Show a fallback message to the user
      this.openSnackbar({
        message: this.translateService.instant('billing.inviteSuccessMsg', { invitedCount: invitedEmails.length }),
        action: this.translateService.instant('billing.goToUsers'),
        onActionCallback: () => this.router.navigate(['/manage-users']),
        duration: USERS_SNACKBAR_DURATION,
      });
    }
  }

  public async onCancelRequestSent() {
    const pollCondition = ({ cancellationRequest, subscription }: BillingDetailsApiData) =>
      this.hasActiveCancellationRequest(cancellationRequest)
      || this.hasActiveCancellationRequest(subscription?.cancellationRequest);

    try {
      const billingDetails = await lastValueFrom(this.billingService.pollBillingDetailsUntilConditionMet(pollCondition));
      this.store.dispatch(new UpdateBillingDetails(billingDetails));
    } catch (e) {
      Sentry.captureMessage('Polling for billing details timed out after cancelling subscription', {
        level: 'error',
        extra: { error: e },
      });
      // Don't show error to user as the cancellation request was already sent
    }
  }

  public openCheckoutDialog({
    checkoutSuccessCallback, socialProof,
    ...rest
  }: OpenCheckoutDialogOptions) {
    const onCheckoutSuccess = checkoutSuccessCallback ?? this.onCardUpdatedCompleted.bind(this);

    try {
      return this.dialog.open<CheckoutComponent, CheckoutDialogData, CheckoutDialogClose>(CheckoutComponent, {
        data: {
          onCheckoutSuccess,
          socialProof,
          ...rest,
        },
        autoFocus: false,
        panelClass: 'checkout-dialog',
      });
    } catch (error) {
      Sentry.captureMessage('Error opening checkout dialog', {
        level: 'error',
        extra: { error },
      });
      throw error;
    }
  }

  public openCheckoutV2Dialog({
    checkoutSuccessCallback, socialProof,
    ...rest
  }: OpenCheckoutDialogOptions) {
    const defaultCallback = this.onCardUpdatedCompleted.bind(this);

    return this.dialog.open<CheckoutV2Component, CheckoutDialogData, CheckoutDialogClose>(CheckoutV2Component, {
      data: {
        checkoutSuccessCallback: checkoutSuccessCallback ?? defaultCallback,
        socialProof,
        ...rest,
      },
      autoFocus: false,
      panelClass: 'checkout-dialog',
    });
  }

  private hasActiveCancellationRequest(request?: CancellationRequest | null): boolean {
    if (!request) {
      return false;
    }

    if (request.keepMyAccount) {
      return false;
    }

    if (typeof request.requestDate === 'string' && request.requestDate.trim().length > 0) {
      return true;
    }

    return (Object.entries(request) as Array<[keyof CancellationRequest, CancellationRequest[keyof CancellationRequest]]>)
      .some(([key, value]) => {
        if (key === 'keepMyAccount' || key === 'requestDate') {
          return false;
        }
        return this.hasMeaningfulValue(value);
      });
  }

  private hasMeaningfulValue(value: CancellationRequest[keyof CancellationRequest]): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return true;
  }

  public openSnackbar({ action, duration = 3000, message, onActionCallback }: OpenSnackbarOptions) {
    try {
      const snackBarRef = this.matSnackBar.open(message, action, {
        duration,
        horizontalPosition: 'left',
        panelClass: 'billing-snackbar',
      });

      if (onActionCallback) {
        snackBarRef.onAction().subscribe(() => {
          try {
            onActionCallback();
          } catch (error) {
            Sentry.captureMessage('Error in snackbar action callback', {
              level: 'error',
              extra: { error },
            });
          }
        });
      }
    } catch (error) {
      Sentry.captureMessage('Error opening snackbar', {
        level: 'error',
        extra: { error },
      });
    }
  }
}
